import { useTablePaginationManager } from "@powerledger/ui-component-lib";
import { subYears } from "date-fns";
import { useCallback, useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  appendErcotVintageHalvesToMretsVintagesFilterValues,
  defaultFilter,
  mapStackedFilterStateToFilterState,
} from "@/app/components/page-header";
import { FilterState } from "@/app/components/page-header/filter/filter.types";
import { useAppContext } from "@/app/context";
import { useRecOptions } from "@/app/hooks";
import { useAllRecOptions } from "@/app/hooks/use-all-rec-options";
import { useAllFeatureToggleValues } from "@/app/hooks/use-feature-toggle/use-all-feature-toggle-values";
import { useHasPermissions } from "@/app/hooks/use-has-permission";
import { UserFeatures } from "@/app/hooks/use-okta-profile-features";
import { getDatePart } from "@/app/lib/date-helpers";
import { formatAttributes, ReturnTypeFormatAttributes } from "@/app/lib/format-attributes";
import { ModalContext } from "@/app/modal-provider";
import {
  RecAttributes,
  RegistryLinkStatus,
  SortOrderInput,
  TradePosition,
  useMarketTradeContractsQuery,
} from "@/app/types/generated/graphql";
import { AggregatedRegistryCode, ANY_REGISTRY_CODE, TraceXSupportedRegistries } from "@/app/types/okta.types";

import { ForwardTradeMarketTableData } from "../components";
import { UseForwardTradeFn } from "./forward-trade.types";
import { getForwardTradeMarketColumns } from "./forward-trade-market-columns";

export const useForwardTrade: UseForwardTradeFn = () => {
  const { t } = useTranslation();

  const { hasFeaturePermission } = useHasPermissions({ skipQuery: true });
  const forwardTradeEnabled = useMemo(
    () => hasFeaturePermission([UserFeatures.FORWARD_TRADE_ENABLED]),
    [hasFeaturePermission],
  );
  const {
    toggles: { enableCrossRegistryForwardTrading },
    loading: featureTogglesLoading,
  } = useAllFeatureToggleValues();
  const [activeRegistryCode, setActiveRegistryCode] = useState<AggregatedRegistryCode>(ANY_REGISTRY_CODE);
  const [activeFilters, setActiveFilters] = useState<FilterState>(defaultFilter);
  const { showAcceptForwardTradeModal } = useContext(ModalContext);

  const { pageInfo, offset, fetchData, sort, setTotalItems, resetPage } = useTablePaginationManager<
    ForwardTradeMarketTableData,
    SortOrderInput[]
  >({});
  const { userAccountInfo, userSelections } = useAppContext();

  const { recOptions, loading: recOptionsLoading } = useRecOptions({
    registryCode: enableCrossRegistryForwardTrading
      ? activeRegistryCode
      : (userSelections?.registry?.registryCode as AggregatedRegistryCode),
  });

  const { mergedRecOptions, recOptionsByRegistryCode, loading: allRecOptionsLoading } = useAllRecOptions();

  const { data, loading, refetch } = useMarketTradeContractsQuery({
    fetchPolicy: "cache-and-network",
    variables: {
      input: {
        paginationInput: {
          limit: pageInfo.pageSize ?? 20,
          offset,
        },
        sortOrderInputs: sort,
        where: {
          recAttributes: {
            vintages: enableCrossRegistryForwardTrading
              ? appendErcotVintageHalvesToMretsVintagesFilterValues(activeFilters.vintages, activeRegistryCode)
              : activeFilters.vintages,
            eligibilities: activeFilters.eligibilities,
            locations: activeFilters.locations,
            fuelSources: activeFilters.fuelSources,
            commencementDateGte: activeFilters.cod?.length
              ? getDatePart(subYears(new Date(), +activeFilters.cod?.[0]))
              : undefined,
            certifications: activeFilters.certifications,
            certificateTypes: activeFilters.certificateType,
          },
          tradeSettlementDate: activeFilters.selectedDate[0],
          registryCode: enableCrossRegistryForwardTrading ? activeRegistryCode : userSelections?.registry?.registryCode,
        },
      },
    },
    skip: !forwardTradeEnabled || featureTogglesLoading,
    onCompleted(data) {
      setTotalItems(data?.marketTradeContractQuery?.offsetInfo?.total ?? 0);
    },
  });

  //separating market table columns for preventing maintainability issues : Maintainability rating C
  const tableColumns = useMemo(
    () =>
      getForwardTradeMarketColumns(
        t,
        {
          id: userAccountInfo?.id,
          accountType: userAccountInfo?.accountType,
          linkedRegistries: userAccountInfo?.linkedRegistries?.filter(
            (registry) => registry.status === RegistryLinkStatus.Registered,
          ),
        },
        showAcceptForwardTradeModal,
        enableCrossRegistryForwardTrading,
      ),
    [
      t,
      userAccountInfo?.id,
      userAccountInfo?.accountType,
      showAcceptForwardTradeModal,
      enableCrossRegistryForwardTrading,
      userAccountInfo?.linkedRegistries,
    ],
  );

  const tableData = useMemo(() => {
    const forwardTableData: ForwardTradeMarketTableData = [];

    const queryData = data?.marketTradeContractQuery.marketTradeContracts;

    if (!queryData) return forwardTableData;
    for (const contract of queryData) {
      const attributes = contract.attributes as RecAttributes;
      const options = enableCrossRegistryForwardTrading
        ? recOptionsByRegistryCode[contract.registryCode as TraceXSupportedRegistries] ?? mergedRecOptions
        : recOptions;

      const formattedAttributes = options
        ? formatAttributes({
            attributes,
            /** For forward trades always display "Any" in case of attribute is not specified, regardless of Bid or Ask */
            position: TradePosition.Bid,
            options,
          })
        : ({} as ReturnTypeFormatAttributes);

      forwardTableData.push({
        ...contract,
        attributes,
        formattedAttributes: {
          ...attributes,
          vintages: formattedAttributes.vintages,
          eligibilities: formattedAttributes.eligibilities,
          certifications: formattedAttributes.certifications,
          fuelSources: formattedAttributes.fuelSources,
          locations: formattedAttributes.locations,
          projects: formattedAttributes.projects,
          certificationsAndEligibilities: formattedAttributes.certificationsAndEligibilities,
          certificateTypes: formattedAttributes.certificateTypes,
        },
      });
    }
    return forwardTableData;
  }, [
    data?.marketTradeContractQuery.marketTradeContracts,
    recOptions,
    enableCrossRegistryForwardTrading,
    recOptionsByRegistryCode,
    mergedRecOptions,
  ]);

  const reloadForwardTrades = useCallback(() => {
    refetch();
  }, [refetch]);

  const handleFilterChange = useCallback(
    (arg) => {
      setActiveFilters((activeFilters) => ({ ...activeFilters, ...mapStackedFilterStateToFilterState(arg) }));
      resetPage();
    },
    [resetPage],
  );

  return {
    forwardTradeEnabled,
    tableData,
    loading: loading || recOptionsLoading || allRecOptionsLoading,
    reloadForwardTrades,
    pageInfo,
    fetchData,
    handleFilterChange,
    tableColumns,
    activeRegistryCode: enableCrossRegistryForwardTrading
      ? activeRegistryCode
      : (userSelections?.registry?.registryCode as AggregatedRegistryCode),
    setActiveRegistryCode,
    enableCrossRegistryForwardTrading,
  };
};
